import './SellerNdaTermSelectionForm.scss';
import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { Label } from 'reactstrap';
import { Feedback, Field, Form, Input } from '@availity/form';
import * as yup from 'yup';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useStateSelector } from '../../../../store/selectors';
import { MarketplaceDocumentSectionFieldValidator } from '../DocumentSectionField/MarketplaceDocumentSectionFieldValidator';
import { MarketplaceDocumentSectionField } from '../DocumentSectionField/MarketplaceDocumentSectionField';
import SectionEditorModal from '../../SectionEditorModal/SectionEditorModal';
import {
    defaultTimelineAction,
    TimelineAction,
} from '../../../../slices/marketplace/timeline/models/timeline-actions.model';
import {
    createDefaultDocumentDetails,
    DocumentDetails,
} from '../../../../slices/marketplace/timeline/models/document-details.model';
import { LinkedDataRequestProject } from '../../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import {
    NdaTermSelectionOptionsList,
    TimelineActionType,
} from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { DocumentSection } from '../../../../slices/marketplace/timeline/models/document-section.model';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { NdaTermSelectionDocumentTemplatesState } from '../../../../slices/marketplace/timeline/models/nda-term-selection';
import { LoiAndDdNdaExecutionConfig } from '../../../../slices/marketplace/timeline/models/loi-and-dd-nda-execution-config.model';
import { BuyerExpressionOfInterestSettings } from '../../../../slices/marketplace/timeline/models/buyer-expression-of-interest-settings.model';
import { generateGuid } from '../../../../utils/typedHelper';

interface SellerNdaTermSelectionFormProps {
    party: TimelineParty;
    onSubmit(
        action: TimelineAction,
        masterDocumentDetails?: DocumentDetails,
        additionalDocumentDetails?: DocumentDetails[],
        questionnaire?: LinkedDataRequestProject,
        ndaTermSelection?: NdaTermSelectionDocumentTemplatesState,
        expressionOfInterestSettings?: BuyerExpressionOfInterestSettings,
        loiAndDdNdaExecutionConfig?: LoiAndDdNdaExecutionConfig
    ): void;
    disabled: boolean;
    documentDetails?: DocumentDetails;
    documentDetailsList: DocumentDetails[];
    documentsSections: DocumentSection[];
    setDocumentSectionList(sections: DocumentSection[]): void;
    timelineEvents: TimelineEvent[];
    currentEvent: TimelineEvent;
    linkedAction?: TimelineAction;
    ndaTermSelectionState: NdaTermSelectionDocumentTemplatesState;
    loiAndDdNdaExecutionConfigState?: LoiAndDdNdaExecutionConfig | null;
    fixedActionType?: TimelineActionType;
}

export const SellerNdaTermSelectionForm = forwardRef(
    (props: SellerNdaTermSelectionFormProps, ref) => {
        const formRef = useRef<any>();

        useImperativeHandle(ref, () => ({
            handleSubmit() {
                formRef?.current?.handleSubmit();
            },
        }));

        const documentTemplates = useStateSelector(
            (state) => state.timelines.documentTemplatesTable.templates
        );

        const [masterDocumentDetails, setMasterDocumentDetails] = useState({
            ...(props.documentDetails ?? createDefaultDocumentDetails(true)),
        });

        const [isSignedSectionEditorVisible, setIsSignedSectionEditorVisible] =
            useState(false);

        const isLoiPrep =
            props.fixedActionType ===
            TimelineActionType.SellerCustomSelectLoiAndDdNdaTerms;

        const initialActionType = props.fixedActionType
            ? props.fixedActionType
            : NdaTermSelectionOptionsList.some(
                  (s) => s.value === props.linkedAction?.actionType
              )
                ? props.linkedAction?.actionType
                : TimelineActionType.SellerCustomSelectDataNdaTerms;

        const actionTypeValidation = props.fixedActionType
            ? yup
                  .number()
                  .uniqueActionValidation(
                      props.timelineEvents ?? [],
                      props.currentEvent
                  )
            : yup
                  .number()
                  .uniqueGroupValidation(
                      props.timelineEvents ?? [],
                      props.currentEvent
                  )
                  .uniqueActionValidation(
                      props.timelineEvents ?? [],
                      props.currentEvent
                  );

        return (
            <div className="nda-term-selection-form">
                <Form
                    innerRef={formRef}
                    initialValues={{
                        actionType: initialActionType,
                        noNsTemplate: isLoiPrep
                            ? (props.loiAndDdNdaExecutionConfigState?.noNsTemplateId ?? 0)
                            : (props.ndaTermSelectionState?.noNsTemplateId ?? 0),
                        dvmAndStaffNsTemplate: isLoiPrep
                            ? (props.loiAndDdNdaExecutionConfigState?.dvmAndStaffNsTemplateId ?? 0)
                            : (props.ndaTermSelectionState?.dvmAndStaffNsTemplateId ?? 0),
                        dvmNsTemplate: isLoiPrep
                            ? (props.loiAndDdNdaExecutionConfigState?.dvmNsTemplateId ?? 0)
                            : (props.ndaTermSelectionState?.dvmNsTemplateId ?? 0),
                        staffNsTemplate: isLoiPrep
                            ? (props.loiAndDdNdaExecutionConfigState?.staffNsTemplateId ?? 0)
                            : (props.ndaTermSelectionState?.staffNsTemplateId ?? 0),
                        documentName: isLoiPrep
                            ? (props.loiAndDdNdaExecutionConfigState?.ddNdaDocumentName ?? masterDocumentDetails.name)
                            : masterDocumentDetails.name,
                        documentSection: masterDocumentDetails.sectionId,
                        signedSection:
                            props.loiAndDdNdaExecutionConfigState
                                ?.signedSectionId ?? '-1',
                        signedLoiDocumentName:
                            props.loiAndDdNdaExecutionConfigState?.signedLoiDocumentName ?? '',
                    }}
                    validationSchema={yup.object().shape({
                        actionType: actionTypeValidation,
                        noNsTemplate: yup
                            .number()
                            .min(1, 'Template is required'),
                        dvmAndStaffNsTemplate: yup
                            .number()
                            .min(1, 'Template is required'),
                        dvmNsTemplate: yup
                            .number()
                            .min(1, 'Template is required'),
                        staffNsTemplate: yup
                            .number()
                            .min(1, 'Template is required'),
                        documentName: yup
                            .string()
                            .trim()
                            .required('Document name is required'),
                        documentSection:
                            MarketplaceDocumentSectionFieldValidator.documentSectionValidationRules(
                                props.documentsSections,
                                props.party
                            ),
                        signedSection: isLoiPrep
                            ? yup
                                  .string()
                                  .required('Executed LOI Document Section is required')
                                  .notOneOf(
                                      ['-1'],
                                      'Executed LOI Document Section is required'
                                  )
                            : yup.string().nullable(),
                        signedLoiDocumentName: isLoiPrep
                            ? yup.string().required('Signed LOI Document Name is required')
                            : yup.string().nullable(),
                    })}
                    onSubmit={async (values) => {
                        let docDetails = masterDocumentDetails;

                        docDetails = {
                            ...docDetails,
                            name: values.documentName.trim(),
                            counterParty: TimelineParty.VetValue,
                            templateId: null,
                            sectionId: values.documentSection,
                        };

                        const loiConfigId = isLoiPrep
                            ? props.loiAndDdNdaExecutionConfigState?.id ??
                              generateGuid()
                            : null;

                        props.onSubmit(
                            {
                                ...defaultTimelineAction,
                                actionType: Number(values.actionType),
                                referenceItemId: docDetails.id,
                                loiAndDdNdaExecutionConfigId: loiConfigId,
                            },
                            docDetails,
                            undefined,
                            undefined,
                            isLoiPrep
                                ? undefined
                                : {
                                      id: generateGuid(),
                                      noNsTemplateId: values.noNsTemplate,
                                      dvmAndStaffNsTemplateId:
                                          values.dvmAndStaffNsTemplate,
                                      dvmNsTemplateId: values.dvmNsTemplate,
                                      staffNsTemplateId: values.staffNsTemplate,
                                  },
                            undefined,
                            isLoiPrep
                                ? {
                                      id: loiConfigId,
                                      signedSectionId: values.signedSection,
                                      ddNdaSectionId: values.documentSection,
                                      ddNdaDocumentName: values.documentName.trim(),
                                      signedLoiDocumentName: values.signedLoiDocumentName,
                                      noNsTemplateId: values.noNsTemplate,
                                      dvmAndStaffNsTemplateId:
                                          values.dvmAndStaffNsTemplate,
                                      dvmNsTemplateId: values.dvmNsTemplate,
                                      staffNsTemplateId: values.staffNsTemplate,
                                  }
                                : undefined
                        );
                    }}>
                    <div className="nda-term-selection">
                        {!props.fixedActionType && (
                            <>
                                <Label for="documentTemplate">NDA Type</Label>
                                <Input
                                    type="select"
                                    name="actionType"
                                    disabled={props.disabled}>
                                    {NdaTermSelectionOptionsList.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Input>
                                <Feedback name="actionType" />
                            </>
                        )}
                        {isLoiPrep && (
                            <div className="marketplace-document-section-field">
                                <Label for="signedSection">
                                    Executed LOI Document Section
                                </Label>
                                <FontAwesomeIcon
                                    className="cursor-pointer"
                                    onClick={() =>
                                        setIsSignedSectionEditorVisible(true)
                                    }
                                    icon={faGear}
                                />
                                <Input
                                    type="select"
                                    name="signedSection"
                                    disabled={props.disabled}>
                                    <option value={-1}>Select</option>
                                    {props.documentsSections.map((option) => (
                                        <option
                                            key={`signed-section-${option.id}`}
                                            value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                                </Input>
                                <Feedback name="signedSection" />
                                {isSignedSectionEditorVisible && (
                                    <SectionEditorModal
                                        documentsSections={props.documentsSections}
                                        isVisible={isSignedSectionEditorVisible}
                                        onClose={() =>
                                            setIsSignedSectionEditorVisible(false)
                                        }
                                        setDocumentSectionList={
                                            props.setDocumentSectionList
                                        }
                                        documentDetailsList={
                                            props.documentDetailsList
                                        }
                                        sectionsModalMode={'built-in'}
                                    />
                                )}
                            </div>
                        )}
                        {isLoiPrep && (
                            <Field
                                className="field"
                                name="signedLoiDocumentName"
                                label="Signed LOI Document Name"
                                disabled={props.disabled}
                            />
                        )}
                        <MarketplaceDocumentSectionField
                            documentsSections={props.documentsSections}
                            disabled={props.disabled}
                            setDocumentSectionList={
                                props.setDocumentSectionList
                            }
                            documentDetailsList={props.documentDetailsList}
                            type="sellers"
                            sectionsModalMode={'built-in'}
                            label={isLoiPrep ? 'DD NDA Section' : undefined}
                        />
                        <Label for="noNsTemplate">
                            {isLoiPrep
                                ? 'DD NDA Document vNoNS Template'
                                : 'Document vNoNS Template'}
                        </Label>
                        <Input
                            type="select"
                            name="noNsTemplate"
                            disabled={props.disabled}>
                            <option value={0}>Select</option>
                            {documentTemplates.map((option) => (
                                <option
                                    key={`template-${option.id}`}
                                    value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Input>
                        <Feedback name="noNsTemplate" />

                        <Label for="dvmAndStaffNsTemplate">
                            {isLoiPrep
                                ? 'DD NDA Document vDVM&StaffNS Template'
                                : 'Document vDVM&StaffNS Template'}
                        </Label>
                        <Input
                            type="select"
                            name="dvmAndStaffNsTemplate"
                            disabled={props.disabled}>
                            <option value={0}>Select</option>
                            {documentTemplates.map((option) => (
                                <option
                                    key={`template-${option.id}`}
                                    value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Input>
                        <Feedback name="dvmAndStaffNsTemplate" />

                        <Label for="dvmNsTemplate">
                            {isLoiPrep
                                ? 'DD NDA Document vDVMNS Template'
                                : 'Document vDVMNS Template'}
                        </Label>
                        <Input
                            type="select"
                            name="dvmNsTemplate"
                            disabled={props.disabled}>
                            <option value={0}>Select</option>
                            {documentTemplates.map((option) => (
                                <option
                                    key={`template-${option.id}`}
                                    value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Input>
                        <Feedback name="dvmNsTemplate" />

                        <Label for="staffNsTemplate">
                            {isLoiPrep
                                ? 'DD NDA Document vStaffNS Template'
                                : 'Document vStaffNS Template'}
                        </Label>
                        <Input
                            type="select"
                            name="staffNsTemplate"
                            disabled={props.disabled}>
                            <option value={0}>Select</option>
                            {documentTemplates.map((option) => (
                                <option
                                    key={`template-${option.id}`}
                                    value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Input>
                        <Feedback name="staffNsTemplate" />

                        <Field
                            className="field"
                            name="documentName"
                            label={isLoiPrep ? 'DD NDA Document Name' : 'Document Name'}
                            disabled={props.disabled}
                        />
                    </div>
                </Form>
            </div>
        );
    }
);
